import { Box, alpha } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { CustomCellProps } from 'components/common/grid/';
import { getCellWithTooltip, getIcon } from './index';
import { useClipboard } from 'use-clipboard-copy';
import { renderHtml } from 'tools/safeHtml';
import { escapeHtml } from 'tools/tools';
import toast from 'app/toast';
import useBundleTranslation from 'i18n';

export default function CopyTextCell({ text, subText, icon, rawHtml, align, value, color }: CustomCellProps) {
    const { t } = useBundleTranslation();
    const clipboard = useClipboard();
    const textRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [showTooltip, setShowTooltip] = useState(false);

    const waitForTextRender = (maxTries = 5, delay = 100) => {
        let tries = 0;

        const tryCheck = () => {
            if (!textRef.current || !wrapperRef.current) return;

            const textEl = textRef.current;
            const wrapperEl = wrapperRef.current;

            const textWidth = textEl.scrollWidth;
            const containerWidth = wrapperEl.clientWidth;

            if (textWidth > 0) {
                setShowTooltip(textWidth > containerWidth && !!text);
            } else if (tries < maxTries) {
                tries++;
                setTimeout(tryCheck, delay);
            } else {
                setShowTooltip(false);
            }
        };

        tryCheck();
    };

    useEffect(() => {
        waitForTextRender();

        const wrapperEl = wrapperRef.current;
        if (!wrapperEl) return;

        const resizeObserver = new ResizeObserver(() => {
            waitForTextRender();
        });

        resizeObserver.observe(wrapperEl);

        return () => {
            resizeObserver.disconnect();
        };
    }, [text]);

    const onCopy = () => {
        if (!text) return;
        const copyValue = (value ?? text).toString();
        clipboard.copy(copyValue);
        toast('success', t('copied'), t('copied_to_clipboard'));
    };

    const renderedText = rawHtml ? (
        renderHtml(text ? String(text) : '')
    ) : (
        <>
            {getIcon(icon, text ? { mr: 0.5 } : undefined)}
            {text}
        </>
    );

    const ellipsisBox = (
        <Box
            ref={textRef}
            sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
                minWidth: 0,
                color,
                display: 'inline-block',
                verticalAlign: 'top',
                py: 1,
                align: align ? align : 'left',
            }}
        >
            {renderedText}
        </Box>
    );

    return (
        <Box
            ref={wrapperRef}
            sx={{
                width: '100%',
                minWidth: 0,
                overflow: 'hidden',
                display: 'block',
                cursor: 'copy',
            }}
            onClick={onCopy}
        >
            {/* The tooltip repeats the cell: markup only when the cell itself is markup, otherwise its text, escaped so
                the shared tooltip shows it as written (an answer's table cell is model text; security unit 7). */}
            {showTooltip ? (
                <Box>
                    {getCellWithTooltip(ellipsisBox, rawHtml ? String(text) : escapeHtml(String(text ?? '')), true)}
                </Box>
            ) : (
                ellipsisBox
            )}

            {subText && (
                <Box
                    sx={{
                        fontSize: '0.8rem',
                        color: (theme) => alpha(theme.palette.text.primary, 0.64),
                        mt: 0.5,
                    }}
                >
                    {subText}
                </Box>
            )}
        </Box>
    );
}
